import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Download, Lock, Eye } from "lucide-react";
import { STAGES } from "../data/mockData";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";
import { Markdown } from "../components/Markdown";
import { LearningCompanion } from "../components/LearningCompanion";
import { toast } from "sonner";

export const youtubeId = (url) => {
  if (!url) return null;
  const m = String(url).trim().match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

export const SECTION_META = [
  { key: "enter", label: "Enter", lead: "Arrive" },
  { key: "teaching", label: "Teaching", lead: "Receive" },
  { key: "living_witness", label: "The Living Witness", lead: "A voice from the path" },
  { key: "ask", label: "Ask", lead: "Question" },
  { key: "wait", label: "Wait", lead: "Stay here a while" },
  { key: "engage", label: "Engage", lead: "Encounter" },
  { key: "reflect", label: "Reflect", lead: "Your private journal" },
  { key: "embody", label: "Embody", lead: "Practice" },
  { key: "advance", label: "Advance", lead: "Move on when ready" },
];

const card = { background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" };

export default function CourseDetail() {
  const { courseId } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const { user, refreshMemberData, membership } = useApp();
  const [course, setCourse] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [enrollment, setEnrollment] = useState(null);
  const [completedIds, setCompletedIds] = useState(new Set());

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const url = preview ? `/admin/learning/courses/${courseId}/preview` : `/content/courses/${courseId}`;
        const { data } = await api.get(url);
        if (!ignore) { setCourse(data.course); setEncounters(data.lessons || []); setAccess(data.access || null); }
      } catch {}
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [courseId, preview, user, membership?.has_access]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!user || !course || preview) return;
      try {
        await api.post("/me/enrollments", { course_id: course.id });
        const [{ data: enr }, { data: comps }] = await Promise.all([
          api.get("/me/enrollments"), api.get(`/me/completions?course_id=${course.id}`)]);
        if (ignore) return;
        const mine = (enr || []).find((e) => e.course_id === course.id);
        const ids = new Set((comps || []).map((c) => c.lesson_id).filter(Boolean));
        const legacyIdx = new Set((comps || []).filter((c) => !c.lesson_id).map((c) => c.lesson_index));
        encounters.forEach((e) => { if (legacyIdx.has(e.index)) ids.add(e.id); });
        setCompletedIds(ids);
        if (mine) {
          setEnrollment(mine);
          let i = encounters.findIndex((e) => e.id === mine.current_lesson_id);
          if (i < 0) i = encounters.findIndex((e) => e.index === mine.current_lesson_index);
          setActive(Math.max(0, i));
        }
      } catch {}
    })();
    return () => { ignore = true; };
  }, [user, course, encounters, preview]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-14 text-center" style={{ color: "var(--text-muted)" }}>Loading…</div>;
  if (!course) return (
    <div className="max-w-7xl mx-auto px-4 py-14 text-center">
      <div className="font-serif text-2xl mb-2" style={{ color: "var(--burgundy)" }}>Course not found</div>
      <Link to="/learn" className="ink-link text-sm" style={{ color: "var(--burgundy)" }}>Return to the Learning Circle →</Link>
    </div>
  );

  const stage = STAGES.find((s) => s.step === course.stage) || STAGES[0];
  const current = encounters[active];

  const onCompleted = (data) => {
    setCompletedIds((s) => new Set([...s, current.id]));
    setEnrollment((e) => ({ ...(e || {}), progress: data.progress, current_lesson_id: current.id }));
    refreshMemberData();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-14" data-testid="course-detail-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <Link to="/learn" data-testid="course-back-link" className="inline-flex items-center gap-1 text-sm ink-link" style={{ color: "var(--burgundy)" }}>
          <ArrowLeft className="w-4 h-4" /> Back to Learning Circle
        </Link>
        {preview && (
          <span data-testid="course-preview-banner" className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest" style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
            <Eye className="w-3 h-3" /> Admin preview · drafts visible · progress not saved
          </span>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 min-w-0">
          <CourseHero course={course} stage={stage} count={encounters.length} />

          {course.description && (
            <Markdown className="mb-8" style={{ color: "var(--text-obsidian)" }}>{course.description}</Markdown>
          )}
          <ResourceList resources={course.resources} label="Course resources" />

          {encounters.length === 0 ? (
            <div className="rounded-2xl border p-8 text-center" style={card}>
              <div className="eyebrow mb-2">In preparation</div>
              <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>The Encounters for this course are being prepared.</p>
            </div>
          ) : current.locked ? (
            <LockedEncounter encounter={current} number={active + 1} user={user} />
          ) : (
            <Encounter key={current.id} course={course} encounter={current} number={active + 1}
              user={user} preview={preview} done={completedIds.has(current.id)}
              hasNext={active < encounters.length - 1}
              onNext={() => { setActive((a) => a + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              onCompleted={onCompleted} />
          )}
        </div>

        {encounters.length > 0 && (
          <aside>
            <div className="sticky top-24 rounded-2xl border p-6" style={card}>
              <div className="eyebrow mb-3">Encounters</div>
              <ul className="space-y-1">
                {encounters.map((e, i) => {
                  const done = completedIds.has(e.id);
                  const isActive = i === active;
                  return (
                    <li key={e.id}>
                      <button data-testid={`encounter-item-${i}-btn`} onClick={() => { setActive(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="w-full flex items-start gap-3 rounded-lg p-3 text-left transition-colors"
                        style={{ background: isActive ? "var(--burgundy-tint)" : "transparent" }}>
                        {done ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--burgundy)" }} />
                              : e.locked ? <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--gold-dark)" }} data-testid={`encounter-item-${i}-lock`} />
                              : <Circle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--text-muted)" }} />}
                        <div className="min-w-0">
                          <div className="font-mono text-[10px] tracking-widest uppercase" style={{ color: "var(--gold-dark)" }}>
                            Encounter {String(i + 1).padStart(2, "0")}{e.status && e.status !== "published" ? ` · ${e.status}` : ""}{e.is_free && access && !access.has_access ? " · free" : ""}{e.locked ? " · members" : ""}
                          </div>
                          <div className="text-sm font-medium" style={{ color: isActive ? "var(--burgundy)" : "var(--text-obsidian)" }}>{e.title}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="hairline my-4" />
              <div className="text-xs font-mono" style={{ color: "var(--gold-dark)" }} data-testid="course-progress-label">
                PROGRESS · {enrollment ? enrollment.progress || 0 : 0}%
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function LockedEncounter({ encounter, number, user }) {
  return (
    <article data-testid={`encounter-locked-${encounter.id}`}>
      <header className="mb-8">
        <div className="eyebrow mb-2">Encounter {String(number).padStart(2, "0")} · Learning Circle</div>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight" style={{ color: "var(--burgundy)" }} data-testid="encounter-title">{encounter.title}</h2>
      </header>
      <div className="rounded-2xl p-8 sm:p-10 text-center" style={{ background: "var(--surface-deep)", color: "var(--bg-cream)" }} data-testid="encounter-locked-panel">
        <Lock className="w-6 h-6 mx-auto mb-4" style={{ color: "var(--gold)" }} />
        <p className="font-serif text-xl sm:text-2xl max-w-xl mx-auto leading-snug">This Encounter opens with a Learning Circle membership.</p>
        <p className="mt-4 text-sm opacity-90 max-w-lg mx-auto">Encounter 1 is offered freely. The full curriculum, private reflections within each Encounter, the source library, and the Learning Companion are part of the Learning Circle — $59 a month, cancel anytime.</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to={user ? "/join" : "/login?next=/join"} data-testid="encounter-locked-join-btn" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium" style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
            {user ? "Join the Learning Circle" : "Sign in to join"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </article>
  );
}

function CourseHero({ course, stage, count }) {
  return (
    <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80 mb-8" style={{ background: "var(--burgundy)" }}>
      {course.art_url && <img src={assetUrl(course.art_url)} alt={course.title} className="w-full h-full object-cover" />}
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(48,43,41,0.8), rgba(48,43,41,0.15) 60%)" }} />
      <div className="absolute bottom-5 left-5 right-5" style={{ color: "var(--bg-cream)" }}>
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest mb-3" style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
          Stage {String(course.stage).padStart(2, "0")} · {stage.name}
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl leading-tight" style={{ color: "var(--bg-cream)" }} data-testid="course-title">{course.title}</h1>
        <div className="text-sm mt-1 opacity-90">
          {course.teacher ? `with ${course.teacher}` : ""}{course.duration ? ` · ${course.duration}` : ""} · {count} Encounter{count === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}

function ResourceList({ resources, label }) {
  if (!resources?.length) return null;
  return (
    <div className="rounded-xl border p-4 mb-8" style={card} data-testid="resource-list">
      <div className="eyebrow mb-2">{label}</div>
      <ul className="space-y-1.5">
        {resources.map((r) => (
          <li key={r.id}>
            <a href={assetUrl(`/api/content/learning/resources/${r.id}`)} onClick={(e) => downloadResource(e, r)}
              data-testid={`resource-${r.id}-link`} className="inline-flex items-center gap-2 text-sm ink-link" style={{ color: "var(--burgundy)" }}>
              <Download className="w-3.5 h-3.5" /> {r.title} <span className="text-xs" style={{ color: "var(--text-muted)" }}>{(r.ext || "").replace(".", "").toUpperCase()}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

async function downloadResource(e, r) {
  e.preventDefault();
  try {
    const res = await api.get(`/content/learning/resources/${r.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = r.original_name || r.title; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Sign in as a member to download resources"); }
}

function Encounter({ course, encounter, number, user, preview, done, hasNext, onNext, onCompleted }) {
  const sections = encounter.sections || {};
  const present = useMemo(() => SECTION_META.filter((m) => (sections[m.key] || "").trim() || (m.key === "reflect" && encounter.prompt)), [sections, encounter.prompt]);
  const waitIdx = present.findIndex((m) => m.key === "wait");
  const [waited, setWaited] = useState(waitIdx < 0);
  const [reflectionText, setReflectionText] = useState("");
  const [crisisOpen, setCrisisOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const markComplete = async () => {
    if (preview) { toast("Preview mode — progress is not saved"); return; }
    if (!user) { toast("Sign in to track your journey"); return; }
    try {
      const { data } = await api.post("/me/progress", { course_id: course.id, lesson_index: encounter.index, lesson_id: encounter.id, completed: true });
      onCompleted(data);
      toast("Encounter complete");
    } catch { toast.error("Could not save progress"); }
  };

  const saveReflection = async () => {
    if (preview) { toast("Preview mode — reflections are not saved"); return; }
    if (!user) { toast("Sign in to save your reflections"); return; }
    if (!reflectionText.trim()) { toast("Write a few words first"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/me/reflections", {
        course_id: course.id, lesson_id: encounter.id, lesson_index: encounter.index,
        text: reflectionText, prompt: encounter.prompt || undefined,
      });
      setReflectionText("");
      if (data?.safety?.level === "URGENT") {
        toast.error("A safety resource has been shown. You are not alone.", { duration: 8000 });
        setCrisisOpen(true);
      } else toast("Reflection saved privately to your journey");
    } catch { toast.error("Could not save reflection"); }
    finally { setSaving(false); }
  };

  return (
    <article data-testid={`encounter-${encounter.id}`}>
      <header className="mb-10">
        <div className="eyebrow mb-2">Encounter {String(number).padStart(2, "0")}{done ? " · complete" : ""}</div>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight" style={{ color: "var(--burgundy)" }} data-testid="encounter-title">{encounter.title}</h2>
        {encounter.duration_min && <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>About {encounter.duration_min} minutes, unhurried</div>}
        {encounter.audio_url && <audio controls src={encounter.audio_url} className="mt-4 w-full" data-testid="encounter-audio" />}
        {youtubeId(encounter.video_url) && (
          <div className="mt-5 rounded-2xl overflow-hidden" style={{ aspectRatio: "16 / 9", background: "var(--surface-deep)" }} data-testid="encounter-video">
            <iframe title={`${encounter.title} — lesson video`} src={`https://www.youtube-nocookie.com/embed/${youtubeId(encounter.video_url)}?rel=0&modestbranding=1`}
              className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
          </div>
        )}
      </header>

      {present.length === 0 && (
        <div className="rounded-2xl border p-8 text-center" style={card}>
          <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>This Encounter is being prepared.</p>
        </div>
      )}

      {present.map((m, i) => {
        const hidden = waitIdx >= 0 && i > waitIdx && !waited;
        if (hidden) return null;
        const body = sections[m.key];
        if (m.key === "wait") return <WaitSection key={m.key} body={body} waited={waited} onContinue={() => setWaited(true)} hasMore={i < present.length - 1} />;
        if (m.key === "living_witness") return <WitnessSection key={m.key} body={body} />;
        if (m.key === "reflect") return (
          <ReflectSection key={m.key} body={body} user={user} preview={preview} text={reflectionText} setText={setReflectionText} onSave={saveReflection} saving={saving} />
        );
        if (m.key === "advance") return (
          <AdvanceSection key={m.key} body={body} done={done} hasNext={hasNext} onComplete={markComplete} onNext={onNext} />
        );
        if (m.key === "engage") return (
          <section key={m.key} className="mb-12" data-testid="section-engage">
            <SectionHead meta={m} />
            <Markdown style={{ color: "var(--text-obsidian)" }}>{body}</Markdown>
            <LearningCompanion courseId={course.id} encounterId={encounter.id} user={user} preview={preview} />
          </section>
        );
        return <Section key={m.key} meta={m} body={body} />;
      })}

      {!present.some((m) => m.key === "advance") && present.length > 0 && (waited || waitIdx < 0) && (
        <AdvanceSection body="" done={done} hasNext={hasNext} onComplete={markComplete} onNext={onNext} />
      )}

      <ResourceList resources={encounter.resources} label="Encounter resources" />

      {crisisOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(48,43,41,0.75)" }}>
          <div data-testid="crisis-resource-modal" className="w-full max-w-md rounded-2xl p-6" style={{ background: "var(--bg-cream)", border: "2px solid var(--gold)" }}>
            <div className="eyebrow mb-2" style={{ color: "var(--rose-dark)" }}>You are not alone</div>
            <h3 className="font-serif text-2xl mb-3" style={{ color: "var(--burgundy)" }}>A quiet resource for right now.</h3>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-obsidian)" }}>
              If you may be in immediate danger or believe you may act on thoughts of harming yourself or someone else,
              call <strong>911</strong> or go to the nearest emergency department. In the U.S., you can call or text{" "}
              <strong>988</strong> for the Suicide &amp; Crisis Lifeline.
            </p>
            <button data-testid="crisis-close-btn" onClick={() => setCrisisOpen(false)} className="mt-5 w-full rounded-full py-2.5 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>I've read this</button>
          </div>
        </div>
      )}
    </article>
  );
}

const SectionHead = ({ meta, light }) => (
  <div className="flex items-baseline gap-3 mb-5">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: light ? "var(--gold)" : "var(--gold-dark)" }}>{meta.label}</span>
    <span className="h-px flex-1" style={{ background: light ? "rgba(196,154,58,0.35)" : "var(--border-cream)" }} />
    <span className="font-serif italic text-sm" style={{ color: light ? "rgba(247,240,227,0.7)" : "var(--text-muted)" }}>{meta.lead}</span>
  </div>
);

function Section({ meta, body }) {
  return (
    <section className="mb-12" data-testid={`section-${meta.key}`}>
      <SectionHead meta={meta} />
      <Markdown style={{ color: "var(--text-obsidian)" }}>{body}</Markdown>
    </section>
  );
}

function WitnessSection({ body }) {
  return (
    <section className="mb-12 rounded-2xl border p-6 sm:p-8" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold-soft)" }} data-testid="section-living_witness">
      <SectionHead meta={SECTION_META[2]} />
      <Markdown className="font-serif text-[1.05rem]" style={{ color: "var(--text-obsidian)" }}>{body}</Markdown>
    </section>
  );
}

function WaitSection({ body, waited, onContinue, hasMore }) {
  return (
    <section className="mb-12 rounded-2xl p-7 sm:p-10 text-center" style={{ background: "var(--surface-deep)", color: "var(--bg-cream)" }} data-testid="section-wait">
      <SectionHead meta={SECTION_META[4]} light />
      <div className="mx-auto w-2 h-2 rounded-full my-6" style={{ background: "var(--gold)", boxShadow: "0 0 0 10px rgba(196,154,58,0.12)" }} />
      <Markdown className="font-serif text-lg sm:text-xl max-w-xl mx-auto" style={{ color: "var(--bg-cream)" }}>{body}</Markdown>
      {hasMore && !waited && (
        <button data-testid="wait-continue-btn" onClick={onContinue}
          className="mt-8 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm border transition-colors hover:bg-[rgba(196,154,58,0.12)]"
          style={{ borderColor: "var(--gold)", color: "var(--gold)" }}>
          I have sat with this. Continue when ready.
        </button>
      )}
    </section>
  );
}

function ReflectSection({ body, user, preview, text, setText, onSave, saving }) {
  return (
    <section className="mb-12 rounded-2xl border p-6 sm:p-8" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="section-reflect">
      <SectionHead meta={SECTION_META[6]} />
      <Markdown className="mb-6" style={{ color: "var(--text-obsidian)" }}>{body}</Markdown>
      <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--text-muted)" }}>
        <Lock className="w-3.5 h-3.5" /> Private. Only you can read what you write here.
      </div>
      <textarea data-testid="lesson-journal-input" rows={6} value={text} onChange={(e) => setText(e.target.value)}
        placeholder={user ? "Write freely. This is a sanctuary of your own words." : "Sign in to write and save private reflections."}
        disabled={!user || preview}
        className="w-full rounded-lg p-4 text-sm border resize-y focus:outline-none disabled:opacity-60 leading-relaxed"
        style={{ background: "var(--bg-cream)", borderColor: "var(--border-cream)" }} />
      <button data-testid="lesson-save-journal-btn" onClick={onSave} disabled={saving}
        className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium disabled:opacity-60"
        style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>{saving ? "Saving…" : "Save to my private journal"}</button>
    </section>
  );
}

function AdvanceSection({ body, done, hasNext, onComplete, onNext }) {
  return (
    <section className="mb-12" data-testid="section-advance">
      <SectionHead meta={SECTION_META[8]} />
      <Markdown style={{ color: "var(--text-obsidian)" }}>{body}</Markdown>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button data-testid="lesson-mark-complete-btn" onClick={onComplete} disabled={done}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-70"
          style={{ background: done ? "var(--gold)" : "var(--burgundy)", color: done ? "var(--burgundy)" : "var(--bg-cream)" }}>
          {done ? <><CheckCircle2 className="w-4 h-4" /> Encounter complete</> : "I have completed this Encounter"}
        </button>
        {hasNext && (
          <button data-testid="encounter-next-btn" onClick={onNext}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm border"
            style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
            Next Encounter <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </section>
  );
}
